import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import * as API from "services/api"
import { errorToast, successToast } from "shared/toast"
import { DEFAULT_NAMES } from "utils/customNameHelpers"

const SLICE_NAME = "customNames"

const UNINITIALIZED = "uninitialized"
const INITIALIZING = "initializing"
const LOADED = "loaded"

const INITIAL_STATE = {
  customNames: [],
  status: UNINITIALIZED,
}

let setOrganizationSubdomain

// #####################################################################
// The async thunks below belong to the PHP admin section, where custom
// names are managed.  The rest of the application only reads custom
// names, and populates this slice by dispatching setCustomNames with
// the names rendered into the page.
// #####################################################################

export const initialize = createAsyncThunk(
  `${SLICE_NAME}/initialize`,
  async ({ organizationSubdomain }, thunkAPI) => {
    thunkAPI.dispatch(setOrganizationSubdomain({ organizationSubdomain }))

    const response = await API.phpOrganizationCustomNames({ organizationSubdomain })

    if (!response.ok) {
      throw new Error("Something went wrong. Unable to load custom names.")
    }

    return (
      { customNames: response.data }
    )
  },
)

export const createCustomName = createAsyncThunk(
  `${SLICE_NAME}/createCustomName`,
  async ({ key, name, onSuccess }, thunkAPI) => {
    const { organizationSubdomain } = thunkAPI.getState()[SLICE_NAME]

    const response = await API.phpCreateOrganizationCustomName({ key, name, organizationSubdomain })

    if (!response.ok) {
      throw new Error("Something went wrong. Unable to save the custom name.")
    }

    return (
      {
        customName: response.data,
        onSuccess,
      }
    )
  },
)

export const updateCustomName = createAsyncThunk(
  `${SLICE_NAME}/updateCustomName`,
  async ({ key, name }, thunkAPI) => {
    const { organizationSubdomain } = thunkAPI.getState()[SLICE_NAME]

    const response = await API.phpUpdateOrganizationCustomName({ key, name, organizationSubdomain })

    if (!response.ok) {
      throw new Error("Something went wrong. Unable to save the custom name.")
    }

    return (
      { customName: response.data }
    )
  },
)

export const deleteCustomName = createAsyncThunk(
  `${SLICE_NAME}/deleteCustomName`,
  async ({ key }, thunkAPI) => {
    const { organizationSubdomain } = thunkAPI.getState()[SLICE_NAME]

    const response = await API.phpDeleteOrganizationCustomName({ key, organizationSubdomain })

    if (!response.ok) {
      throw new Error("Something went wrong. Unable to remove the custom name.")
    }

    return (
      { key }
    )
  },
)

export const customNamesSlice = createSlice({
  name: SLICE_NAME,
  initialState: INITIAL_STATE,
  reducers: {
    // How the slice is populated outside the PHP admin section: the names an
    // organization has set are rendered into the page rather than fetched.
    setCustomNames: (state, action) => {
      state.customNames = action.payload
      state.status = LOADED
    },
    setOrganizationSubdomain: (state, action) => {
      const { organizationSubdomain } = action.payload
      state.organizationSubdomain = organizationSubdomain
    },
  },
  // Every case below responds to a PHP admin section thunk.
  extraReducers: (builder) => {
    builder
      .addCase(initialize.pending, (state) => {
        state.status = INITIALIZING
      })
      .addCase(initialize.fulfilled, (state, action) => {
        state.customNames = action.payload.customNames
        state.status = LOADED
      })
      .addCase(initialize.rejected, (state, action) => {
        state.status = LOADED
        errorToast(action.error.message)
      })
      .addCase(createCustomName.fulfilled, (state, action) => {
        const { customName, onSuccess } = action.payload

        state.customNames = [...state.customNames, customName]
        onSuccess?.()
        successToast("Custom name saved successfully.")
      })
      .addCase(createCustomName.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(updateCustomName.fulfilled, (state, action) => {
        const { customName } = action.payload

        const index = state.customNames.findIndex(({ key }) => key === customName.key)
        state.customNames[index] = customName
        successToast("Custom name saved successfully.")
      })
      .addCase(updateCustomName.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(deleteCustomName.fulfilled, (state, action) => {
        const { key } = action.payload

        state.customNames = state.customNames.filter((customName) => customName.key !== key)
        successToast("Custom name removed successfully.")
      })
      .addCase(deleteCustomName.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
  },
})

export const { setCustomNames } = customNamesSlice.actions

setOrganizationSubdomain = customNamesSlice.actions.setOrganizationSubdomain

export const customNames = (state) => state[SLICE_NAME].customNames

export const initializing = (state) => {
  const { status } = state[SLICE_NAME]
  return status === UNINITIALIZED || status === INITIALIZING
}

// The custom name an organization has set for a key, or undefined when it has set none.
export const customNameForKey = (key) => (state) => (
  state[SLICE_NAME].customNames.find((customName) => customName.key === key)
)

// The name a key is read by: the one the organization set, falling back to the default
// for a key it has not renamed.  Only names an organization has set are stored, so this
// is what anything displaying a name reads.
export const nameForKey = (key) => (state) => (
  customNameForKey(key)(state)?.name ?? DEFAULT_NAMES[key]
)

export default customNamesSlice.reducer
